import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sevkiyatService, kullaniciService } from '../api/dbService';
import PageLayout from '../components/PageLayout';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Shipments() {
  const user = JSON.parse(localStorage.getItem('current_user') || '{}');
  const isAdmin = user.role === 'admin';

  const [sevkiyatlar, setSevkiyatlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [durum, setDurum] = useState('');
  const [search, setSearch] = useState('');

  // Atama modal state
  const [ataModal, setAtaModal] = useState(null); // sevkiyat objesi
  const [tumKullanicilar, setTumKullanicilar] = useState([]);
  const [atananlar, setAtananlar] = useState([]);
  const [ataKullaniciId, setAtaKullaniciId] = useState('');
  const [ataLoading, setAtaLoading] = useState(false);
  const [backfillingId, setBackfillingId] = useState(null);

  const fetchData = async () => {
    try {
      const res = await sevkiyatService.getTumSevkiyatlar(durum ? { durum } : {});
      setSevkiyatlar(res.data || []);
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [durum]);

  useEffect(() => {
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, [durum]);

  // Admin için kullanıcı listesini bir kere yükle
  useEffect(() => {
    if (!isAdmin) return;
    kullaniciService.getKullanicilar()
      .then(res => setTumKullanicilar(res.data || []))
      .catch(() => {});
  }, [isAdmin]);

  const filtered = sevkiyatlar.filter(s =>
    !search || [s.sevkiyat_no, s.sevkiyat_adi, s.plaka].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  async function handleDelete(id) {
    if (!window.confirm('Bu sevkiyatı silmek istediğinizden emin misiniz?')) return;
    try {
      await sevkiyatService.deleteSevkiyat(id);
      setSevkiyatlar(prev => prev.filter(s => s.id !== id));
      toast.success('Silindi');
    } catch { toast.error('Silinemedi'); }
  }

  const openAtaModal = useCallback(async (sevkiyat) => {
    setAtaModal(sevkiyat);
    setAtaKullaniciId('');
    setAtaLoading(true);
    try {
      const res = await sevkiyatService.getSevkiyatKullanicilar(sevkiyat.id);
      setAtananlar(res.data || []);
    } catch { setAtananlar([]); }
    finally { setAtaLoading(false); }
  }, []);

  async function handleAta() {
    if (!ataKullaniciId) return toast.error('Kullanıcı seçin');
    try {
      await sevkiyatService.ataSevkiyatKullanici(ataModal.id, ataKullaniciId);
      const res = await sevkiyatService.getSevkiyatKullanicilar(ataModal.id);
      setAtananlar(res.data || []);
      setAtaKullaniciId('');
      toast.success('Kullanıcı atandı');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Atama başarısız');
    }
  }

  async function handleAtaKaldir(uid) {
    try {
      await sevkiyatService.sevkiyatKullaniciSil(ataModal.id, uid);
      const res = await sevkiyatService.getSevkiyatKullanicilar(ataModal.id);
      setAtananlar(res.data || []);
      toast.success('Atama kaldırıldı');
    } catch { toast.error('İşlem başarısız'); }
  }

  async function handleBackfill(id) {
    if (!window.confirm('Bu sevkiyatın cihazı için son 24 saatlik veri çekilsin mi?')) return;
    setBackfillingId(id);
    try {
      const res = await sevkiyatService.gecmisVeriCek(id, 24);
      toast.success(`${res.yazilan}/${res.cekilen} kayıt yazıldı`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Geçmiş veri çekilemedi');
    } finally {
      setBackfillingId(null);
    }
  }

  const atamaYapilmamislar = tumKullanicilar.filter(k => !atananlar.find(a => a.id === k.id));

  return (
    <PageLayout>
      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
        <h5 className="mb-0 fw-semibold">Tüm Sevkiyatlar</h5>
        <div className="d-flex gap-2">
          <select className="form-select form-select-sm" value={durum} onChange={e => setDurum(e.target.value)} style={{ width: 140 }}>
            <option value="">Tümü</option>
            <option value="aktif">Aktif</option>
            <option value="tamamlandi">Tamamlandı</option>
          </select>
          <input className="form-control form-control-sm" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 160 }} />
        </div>
      </div>
      {loading ? <LoadingSpinner /> : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle">
            <thead className="table-light">
              <tr><th>No</th><th>Adı</th><th>Cihaz</th><th>Güzergah</th><th>Durum</th><th>Tarih</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id}>
                  <td><small>{s.sevkiyat_no}</small></td>
                  <td>{s.sevkiyat_adi}</td>
                  <td><code>{s.device_id}</code></td>
                  <td><small>{s.cikis_noktasi} → {s.varis_noktasi}</small></td>
                  <td>
                    <span className={`badge ${s.tamamlandi_mi ? 'bg-secondary' : 'bg-success'}`}>
                      {s.tamamlandi_mi ? 'Tamamlandı' : 'Aktif'}
                    </span>
                  </td>
                  <td><small>{new Date(s.olusturma_tarihi).toLocaleDateString('tr-TR')}</small></td>
                  <td className="d-flex gap-1 flex-wrap">
                    <Link to={`/view/${s.id}`} className="btn btn-outline-primary btn-sm">Detay</Link>
                    {isAdmin && (
                      <>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => openAtaModal(s)}>
                          Kullanıcı
                        </button>
                        <button className="btn btn-outline-info btn-sm"
                          disabled={backfillingId === s.id}
                          onClick={() => handleBackfill(s.id)}>
                          {backfillingId === s.id ? '...' : 'Geçmiş Çek'}
                        </button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(s.id)}>Sil</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">Kayıt yok</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Kullanıcı Atama Modal */}
      {ataModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h6 className="modal-title mb-0">Kullanıcı Ata</h6>
                  <small className="text-muted">{ataModal.sevkiyat_no} — {ataModal.sevkiyat_adi}</small>
                </div>
                <button className="btn-close" onClick={() => setAtaModal(null)} />
              </div>
              <div className="modal-body">
                {ataLoading ? <LoadingSpinner /> : <>
                  {atananlar.length > 0 && (
                    <div className="mb-3">
                      <small className="text-muted fw-semibold d-block mb-1">Atanmış Kullanıcılar</small>
                      <ul className="list-group list-group-flush">
                        {atananlar.map(k => (
                          <li key={k.id} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                            <span className="small">{k.username} <span className="text-muted">({k.email})</span></span>
                            <button className="btn btn-outline-danger btn-sm py-0 px-1"
                              onClick={() => handleAtaKaldir(k.id)}>✕</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="d-flex gap-2">
                    <select className="form-select form-select-sm flex-grow-1"
                      value={ataKullaniciId} onChange={e => setAtaKullaniciId(e.target.value)}>
                      <option value="">— Kullanıcı seç —</option>
                      {atamaYapilmamislar.map(k => (
                        <option key={k.id} value={k.id}>{k.username} ({k.email})</option>
                      ))}
                    </select>
                    <button className="btn btn-brand btn-sm" onClick={handleAta}>Ata</button>
                  </div>
                </>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setAtaModal(null)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
